import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import { validateNonEmptyRule } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import { InputLabel } from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/FieldPanel';
import useComponentLogicJsonSchema from 'dashboard/apps/InquiryTemplateEditor/hooks/useComponentLogicJsonSchema';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ComponentConditionalLogicPanel from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/ComponentConditionalLogicPanel';
import { DEFAULT_LOGIC_RULE_BLOCK } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/constants';
import { ComponentJsonLogicRule } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  formatJSONLogicRule,
  getInvertedLogicKey,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import LogicBlock from 'dashboard/components-lib/LogicBlock';
import { InquiryTemplateVersionStepUi } from 'dashboard/models/inquiry-template-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';
import { UiFormComponentConfig } from 'lib/interfaces/ui-components';

const OPTIONAL_RULE_CONDITIONAL = 'conditional';
const REQUIRED_LOGIC_KEY = getInvertedLogicKey('optional');

const ComponentFieldWrapper = styled(FieldWrapper)`
  margin-bottom: ${Spacing[3]};
`;

interface FormComponentOptionalEditorProps {
  componentConfig: UiFormComponentConfig;
  stepConfig: InquiryTemplateVersionStepUi;
  editable: boolean;
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
}

const FormComponentOptionalEditor = ({
  componentConfig,
  stepConfig,
  editable,
  handleUpdateComponentConfig,
}: FormComponentOptionalEditorProps) => {
  const optionalLogicRule = componentConfig.config.optional?.logic;
  const { rules, isInvertedRule } =
    typeof optionalLogicRule === 'boolean'
      ? {
          rules: DEFAULT_LOGIC_RULE_BLOCK.rules,
          isInvertedRule: false,
        }
      : formatJSONLogicRule(optionalLogicRule as ComponentJsonLogicRule);
  const componentJsonSchema = useComponentLogicJsonSchema(stepConfig, componentConfig);
  const [shouldShowLogicPanel, { enable: openLogicPanel, disable: closeLogicPanel }] =
    useToggle(false);

  const handleUpdateOptionalLogicRule = (
    value: 'true' | 'false' | typeof OPTIONAL_RULE_CONDITIONAL,
  ) => {
    let newOptionalLogicRule;
    switch (value) {
      case 'true':
        newOptionalLogicRule = true;
        break;
      case 'false':
        newOptionalLogicRule = false;
        break;
      case OPTIONAL_RULE_CONDITIONAL:
        // invert new rules by default as it's easier for builders to reason
        // from the framing 'required if' rather than 'optional if'
        newOptionalLogicRule = { '!': DEFAULT_LOGIC_RULE_BLOCK.rules };
        break;
    }
    handleUpdateComponentConfig({
      optional: {
        ...componentConfig.config.optional,
        logic: newOptionalLogicRule,
      },
    });
  };

  return (
    <ComponentFieldWrapper>
      <InputLabel htmlFor='component-fields-editor__field-required-select'>Required</InputLabel>
      <Select
        size='sm'
        id='component-fields-editor__field-required-select'
        value={
          typeof optionalLogicRule === 'boolean'
            ? optionalLogicRule.toString()
            : OPTIONAL_RULE_CONDITIONAL
        }
        onValueChange={handleUpdateOptionalLogicRule}
        disabled={!editable}
      >
        {/* the non-conditional value/display option pairs are inverse here because
      the user-facing label is 'Required' but the underlying attribute is 'optional' */}
        <Option value='false'>Yes</Option>
        <Option value={OPTIONAL_RULE_CONDITIONAL}>Conditional</Option>
        <Option value='true'>No</Option>
      </Select>
      {typeof optionalLogicRule !== 'boolean' && (
        <>
          <Spacer height={1} />
          <div
            id='component-optional__logic-display'
            style={{ cursor: 'pointer' }}
            onClick={openLogicPanel}
          >
            {rules.or.map((rule, index) => (
              <LogicBlock
                key={index}
                keyPrefix='component_optional'
                andRules={rule}
                prefixText={index === 0 ? (isInvertedRule ? 'Require if' : 'Optional if') : 'Or'}
                orIndex={index}
                validateRule={validateNonEmptyRule}
              />
            ))}
          </div>
          {shouldShowLogicPanel && (
            <ComponentConditionalLogicPanel
              title='Require field'
              description='Configure logic rule evaluated in real time referencing inputs on the current screen.'
              selectedLogicKey='optional'
              logicKeyOptions={[
                {
                  key: REQUIRED_LOGIC_KEY,
                  label: 'Require field',
                  disabled: false,
                },
              ]}
              conditional={{
                rules,
              }}
              logicRuleSection='client'
              jsonSchema={componentJsonSchema['client']}
              editable={editable}
              isInvertedRule={isInvertedRule}
              onOutsideClick={closeLogicPanel}
              updateConditional={(conditional) =>
                handleUpdateComponentConfig({
                  optional: {
                    ...componentConfig.config.optional,
                    logic: conditional,
                  },
                })
              }
            />
          )}
        </>
      )}
    </ComponentFieldWrapper>
  );
};

export default FormComponentOptionalEditor;
